import { artifactFilename, isArtifactElement } from '@/lib/artifacts';
import { cn } from '@/lib/utils';
import { useRecoilValue } from 'recoil';

import { type IMessageElement, sideViewState } from '@chainlit/react-client';

export const FILE_REFERENCE_CHIP_CLASS =
    'text-reference [-webkit-text-stroke:0.025em]';

const OPENABLE_CHIP_CLASS =
    'cursor-pointer rounded-sm hover:text-reference-hover hover:underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2';

const FILE_REFERENCE_IN_TEXT = /(^|\s)(#[^\s#]+\.[A-Za-z0-9]{1,8})(?=\s|$)/g;
const FILE_ELEMENT_TYPES = ['file', 'pdf', 'image', 'video'];

export const openReferencedFile = (elements: IMessageElement[], filename: string) => {
    const artifact = elements
        .filter(isArtifactElement)
        .filter((el) => artifactFilename(el) === filename)
        .pop();
    if (artifact) {
        return () =>
            window.dispatchEvent(
                new CustomEvent('openArtifactsSidebar', {
                    detail: { elementId: artifact.id }
                })
            );
    }
    const file = elements
        .filter(
            (el) => FILE_ELEMENT_TYPES.includes(el.type) && el.name === filename
        )
        .pop();
    if (file) {
        return () =>
            window.dispatchEvent(
                new CustomEvent('openUploadsSidebar', {
                    detail: { elementId: file.id }
                })
            );
    }
    return undefined;
};

interface Props {
    text: string;
}

export const FileReferenceText = ({ text }: Props) => {
    const elements = useRecoilValue(sideViewState)?.elements ?? [];
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    for (const match of text.matchAll(FILE_REFERENCE_IN_TEXT)) {
        const start = match.index! + match[1].length;
        const reference = match[2];
        const open = openReferencedFile(elements, reference.slice(1));
        parts.push(text.slice(lastIndex, start));
        parts.push(
            open ? (
                <button
                    key={start}
                    type="button"
                    onClick={open}
                    className={cn(FILE_REFERENCE_CHIP_CLASS, OPENABLE_CHIP_CLASS)}
                >
                    {reference}
                </button>
            ) : (
                <span key={start} className={FILE_REFERENCE_CHIP_CLASS}>
                    {reference}
                </span>
            )
        );
        lastIndex = start + reference.length;
    }
    parts.push(text.slice(lastIndex));
    return <span>{parts}</span>;
};
